import { create } from 'zustand';
import axios from '../lib/axios';

export const useAuthStore = create((set, get) => ({
    user: null,
    tenant: null,
    isAuthenticated: !!localStorage.getItem('auth_token'),
    isImpersonating: localStorage.getItem('is_impersonating') === 'true',
    isLoading: true, // Starts true to trigger checkAuth automatically
    error: null,

    // Check if session exists using the token
    checkAuth: async () => {
        const token = localStorage.getItem('auth_token');

        if (!token) {
            set({ user: null, isAuthenticated: false, isLoading: false });
            return;
        }

        try {
            set({ isLoading: true, error: null });
            const response = await axios.get('/api/me');
            set({
                user: response.data.user,
                tenant: response.data.tenant,
                isAuthenticated: true,
                isLoading: false,
            });
        } catch (error) {
            // Solo cerramos sesión si el servidor nos dice explícitamente que el token no es válido
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                const errorDetail = error.response.data?.message || error.message;
                console.error(`Token inválido detectado en checkAuth: ${errorDetail}`);
                
                // Guardar para diagnóstico antes de limpiar
                sessionStorage.setItem('auth_debug_checkauth', JSON.stringify({
                    status: error.response.status,
                    message: errorDetail,
                    at: new Date().toISOString()
                }));
                
                localStorage.removeItem('auth_token');
                localStorage.removeItem('is_impersonating');
                localStorage.removeItem('admin_token');
                set({
                    user: null,
                    tenant: null,
                    isAuthenticated: false,
                    isImpersonating: false,
                    isLoading: false,
                });
            } else {
                // Para otros errores (502, 503, errores de red), mantenemos la sesión
                // para que el usuario no pierda el acceso si el servidor se cae un momento.
                console.warn("Error de conexión con el servidor. Manteniendo sesión para reintento.", error.message);
                set({ 
                    isLoading: false,
                });
            }
        }
    },

    // Login credentials and get Bearer Token
    login: async (email, password) => {
        try {
            set({ isLoading: true, error: null });

            // Sincronizar cookie CSRF antes del login (Previene errores 419)
            try {
                await axios.get('/sanctum/csrf-cookie');
            } catch (csrfError) {
                console.warn('No se pudo sincronizar la cookie CSRF, procediendo de todos modos...');
            }

            const response = await axios.post('/api/login', { email, password });
            const { user, token, tenant } = response.data;

            localStorage.setItem('auth_token', token);
            localStorage.removeItem('is_impersonating');
            localStorage.removeItem('admin_token');

            set({
                user: user,
                tenant: tenant,
                isAuthenticated: true,
                isImpersonating: false,
                isLoading: false,
                error: null,
            });

            return true;
        } catch (error) {
            let message = 'Usuario o contraseña inválidos';
            
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !error.response) {
                message = 'Error de red: No se pudo conectar al servidor. Asegúrate de que el backend esté en ejecución (php artisan serve).';
            } else if (error.response?.data?.message) {
                message = error.response.data.message;
            }

            set({
                error: message,
                isLoading: false,
            });
            return false;
        }
    },

    // Logout wrapper
    logout: async () => {
        try {
            set({ isLoading: true });
            await axios.post('/api/logout');
        } catch (error) {
            // Ignore errors on logout
        } finally {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('is_impersonating');
            localStorage.removeItem('admin_token');
            // Limpiar cookies de sesión por si acaso
            document.cookie = "laravel_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
            document.cookie = "XSRF-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
            
            set({
                user: null,
                tenant: null,
                isAuthenticated: false,
                isImpersonating: false,
                isLoading: false,
            });
        }
    },

    // Start impersonating a tenant user
    impersonate: async (userId) => {
        try {
            set({ isLoading: true, error: null });

            // Store original token (super admin)
            const originalToken = localStorage.getItem('auth_token');
            if (originalToken) {
                localStorage.setItem('admin_token', originalToken);
            }

            const response = await axios.post(`/api/super-admin/users/${userId}/impersonate`);
            const { user, token, tenant } = response.data;

            localStorage.setItem('auth_token', token);
            localStorage.setItem('is_impersonating', 'true');

            set({
                user: user,
                tenant: tenant,
                isAuthenticated: true,
                isImpersonating: true,
                isLoading: false,
                error: null,
            });

            return true;
        } catch (error) {
            let message = 'Error al iniciar sesión como el usuario';
            if (error.response?.data?.message) {
                message = error.response.data.message;
            }
            set({
                error: message,
                isLoading: false,
            });
            return false;
        }
    },

    // Stop impersonating and restore super admin session
    leaveImpersonation: async () => {
        try {
            set({ isLoading: true });
            await axios.post('/api/impersonate/leave');
        } catch (error) {
            console.error('Error leaving impersonation on backend:', error);
        } finally {
            const adminToken = localStorage.getItem('admin_token');
            localStorage.removeItem('is_impersonating');
            localStorage.removeItem('admin_token');

            if (adminToken) {
                localStorage.setItem('auth_token', adminToken);
                set({ isLoading: true });
                try {
                    const response = await axios.get('/api/me');
                    set({
                        user: response.data.user,
                        tenant: response.data.tenant,
                        isAuthenticated: true,
                        isImpersonating: false,
                        isLoading: false,
                    });
                } catch (err) {
                    localStorage.removeItem('auth_token');
                    set({
                        user: null,
                        tenant: null,
                        isAuthenticated: false,
                        isImpersonating: false,
                        isLoading: false,
                    });
                }
            } else {
                localStorage.removeItem('auth_token');
                set({
                    user: null,
                    tenant: null,
                    isAuthenticated: false,
                    isImpersonating: false,
                    isLoading: false,
                });
            }
        }
    },

    // Helper clear errors manually on the view
    clearError: () => set({ error: null })
}));
